/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import {
    CheckboxVisibility,
    DetailsHeader,
    DetailsRow,
    IColumn,
    IDetailsColumnRenderTooltipProps,
    IDetailsHeaderProps,
    IDetailsRowProps,
    SelectionMode
} from '@fluentui/react/lib/DetailsList';
import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { Table } from 'src/core/components/Table';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useSelection } from 'src/core/hooks/useSelection';
import { useEntityTree } from 'src/luis/modules/entities/hooks/useEntityTree';
import { useEntityTreeActions } from 'src/luis/modules/entities/hooks/useEntityTreeActions';
import { TreeItemData } from 'src/luis/modules/entities/types/treeItemData';
import { useModels } from 'src/luis/modules/models/useModels';
import { SimpleEntity } from 'src/luis/projects/entities/types/SimpleEntity';
import { entityDenormalizer } from 'src/luis/projects/entities/utils/entitiesDenormalizer';
import { LuisId } from 'src/luis/types/Resource';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { SimpleEntityStore } from 'src/luis/types/stores/entities/SimpleEntityStore';
import { ConversationFeaturePickerContainer } from 'src/luis/verticals/conversationFeaturePicker/ConversationFeaturePickerContainer';
import { DeleteEntityModal } from 'src/luis/verticals/deleteEntityModal/DeleteEntityModal';
import { MLEntityInfoCell } from 'src/luis/verticals/entityDetails/components/MLEntityInfoCell';

/**
 * Localized strings used in the UI
 */
const messages = defineMessages({
    entityInfoColumnName: {
        id: 'entities.mlEntityDetails.entityInfoColumnName',
        defaultMessage: 'Name'
    },
    entityFeaturesColumnName: {
        id: 'entities.mlEntityDetails.entityFeaturesColumnName',
        defaultMessage: 'Machine learning features'
    },
    // placeholder - Melanie to come back with final string
    featuresColumnHelpText: {
        id: 'entities.mlEntityDetails.featuresColumnHelpText',
        defaultMessage: 'An ML entity can be composed of smaller subentities, each of which can have its own ML features.'
    }
});

const entityInfoColumnKey = 'info';
const entityFeaturesColumnKey = 'features';

/**
 * Props for MLEntityDetails
 */
export type MLEntityDetailsProps = {
    entityStore: SimpleEntityStore;

    /**
     * User role to determine the actions.
     */
    userRole: string;

    /**
     * User role to show in LuisTooltip message.
     */
    displayedUserRole: string;
};

export const MLEntityDetails = observer((props: MLEntityDetailsProps) => {
    const { entityStore, userRole, displayedUserRole } = props;

    /**
     * ****************** Hooks ******************
     */

    const { localize } = useLocalization();

    const { entityItems, setNewChildParentId, toggleCollapse } = useEntityTree([entityStore]);
    const { createChildEntity, renameEntity, renameChildEntity, deleteEntity } = useEntityTreeActions();
    const { selection } = useSelection<TreeItemData<SimpleEntityStore>>(SelectionMode.single);
    const { entityStores, intentStores, phraseListStores } = useModels();
    const denormalizer = entityDenormalizer(entityStores, intentStores, phraseListStores);

    const [childEntityToDelete, setChildEntityToDelete] = React.useState<SimpleEntityStore>(null);

    const deleteEntityTracker = useProgressTracker({
        onComplete: () => {
            setChildEntityToDelete(null);
        }
    });

    /**
     * ****************** Handlers ******************
     */

    const handleDeleteEntity = (_: LuisId, entityToDelete: SimpleEntityStore) => {
        setChildEntityToDelete(entityToDelete);
    };

    /**
     * ****************** Render ******************
     */

    // [sorgh]: This is a hack to trick mobx into observing a recursive observable.
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    entityStore.updateCount;

    const columns: IColumn[] = [
        {
            key: entityInfoColumnKey,
            name: localize(messages.entityInfoColumnName),
            minWidth: 300,
            maxWidth: 500,
            isResizable: true,

            onRender: (item: TreeItemData<SimpleEntityStore>, index: number) => (
                <MLEntityInfoCell
                    rootEntityId={item.rootEntityId}
                    parentEntityId={item.parentEntityId}
                    entityStore={item.entityStore as SimpleEntityStore}
                    level={item.level}
                    isCollapsed={item.isCollapsed}
                    kind={item.kind}
                    isSelected={selection.getSelectedIndices().includes(index)}
                    onToggleCollapse={toggleCollapse}
                    onToggleEntityCreation={setNewChildParentId}
                    onCreateEntity={createChildEntity}
                    onRenameEntity={renameEntity}
                    onRenameChildEntity={renameChildEntity}
                    onDeleteEntity={handleDeleteEntity}
                    userRole={userRole}
                    displayedUserRole={displayedUserRole}
                />
            )
        },
        {
            key: entityFeaturesColumnKey,
            name: localize(messages.entityFeaturesColumnName),
            minWidth: 300,
            maxWidth: 500,
            isResizable: true,
            onRender: (item: TreeItemData<SimpleEntityStore>) => (
                <ConversationFeaturePickerContainer
                    telemetryContext="MLEntityDetails"
                    model={denormalizer(item.entityStore) as SimpleEntity}
                    simpleEntityRootId={item.rootEntityId}
                />
            )
        }
    ];

    const renderCustomHeaderTooltip = (tooltipHostProps: IDetailsColumnRenderTooltipProps) => {
        const { column } = tooltipHostProps;

        return (
            <Stack horizontal verticalAlign="center">
                {tooltipHostProps.children}
                {column?.key === entityFeaturesColumnKey && <CalloutButton content={localize(messages.featuresColumnHelpText)} />}
            </Stack>
        );
    };

    const renderRow = (rowProps: IDetailsRowProps) => {
        const rowItem = rowProps.item as TreeItemData<SimpleEntityStore>;

        return <DetailsRow {...rowProps} key={`${rowProps.itemIndex}:${rowItem.kind}`} />;
    };

    return (
        <>
            <Table<TreeItemData<EntityStore>>
                items={entityItems}
                columns={columns}
                selectionMode={SelectionMode.single}
                selection={selection}
                checkboxVisibility={CheckboxVisibility.hidden}
                onRenderRow={renderRow}
                onRenderDetailsHeader={(headerProps: IDetailsHeaderProps) => (
                    <DetailsHeader {...headerProps} onRenderColumnHeaderTooltip={renderCustomHeaderTooltip} />
                )}
            />
            {childEntityToDelete && (
                <DeleteEntityModal
                    entityNames={[childEntityToDelete.name]}
                    disabled={deleteEntityTracker.isRunning}
                    hidden={false}
                    onDelete={() => {
                        deleteEntity(entityStore.id, childEntityToDelete.id, deleteEntityTracker);
                    }}
                    onDismiss={() => setChildEntityToDelete(null)}
                />
            )}
        </>
    );
});
